import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Stethoscope, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import {
  TeamBApi,
  ApiError,
  formatDateTime,
  type CareAdviceResult,
  type ObservationContext,
  type Plant,
  type TimelineDay,
} from '@ai-tiaozs/shared';
import { SafeImage } from '@/components/SafeImage';
import { CareTaskPanel } from '@/components/CareTaskPanel';
import { ObservationAiSummary } from '@/components/ObservationAiSummary';

function friendlyError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'AI_FAILED' || e.message.includes('empty content')) {
      return e.message.includes('暂时') ? e.message : 'AI 暂时没返回诊断结果,请稍后重试';
    }
    return e.message;
  }
  return '生成诊断失败,请稍后重试';
}

function readLastObservationHint(): { plantId?: string; observationId?: string } | null {
  const raw = sessionStorage.getItem('b_last_observation');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as { plantId?: string; observationId?: string };
  } catch {
    return null;
  }
}

/**
 * B 队 · ③ AI 健康诊断
 * 1. 选择植物 → 2. 选择一条观察记录 → 3. 开始 AI 诊断
 */
export function TeamBCareAdvice() {
  const [params] = useSearchParams();
  const obsIdFromUrl = params.get('obsId');
  const autoDiagnoseFromUrl = Boolean(obsIdFromUrl);

  const [plants, setPlants] = useState<Plant[]>([]);
  const [plantId, setPlantId] = useState('');
  const [observations, setObservations] = useState<TimelineDay[]>([]);
  const [selectedObsId, setSelectedObsId] = useState<string | null>(null);
  const [context, setContext] = useState<ObservationContext | null>(null);
  const [result, setResult] = useState<CareAdviceResult | null>(null);

  const [loadingPlants, setLoadingPlants] = useState(true);
  const [loadingObservations, setLoadingObservations] = useState(false);
  const [loadingContext, setLoadingContext] = useState(false);
  const [loadingSavedAdvice, setLoadingSavedAdvice] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initDone, setInitDone] = useState(false);
  const [resultFromSaved, setResultFromSaved] = useState(false);
  const [enrichingIds, setEnrichingIds] = useState<Set<string>>(new Set());

  const sortedObservations = useMemo(
    () =>
      [...observations]
        .filter((o) => o.observationId && (o.photoUrl || o.photoKey))
        .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0)),
    [observations],
  );

  const loadPlants = useCallback(async () => {
    setLoadingPlants(true);
    try {
      const { plants: list } = await TeamBApi.listPlants();
      setPlants(list);
      return list;
    } catch {
      setPlants([]);
      return [];
    } finally {
      setLoadingPlants(false);
    }
  }, []);

  const enrichMissingSnapshots = useCallback(async (days: typeof observations) => {
    const missing = days.filter((d) => d.observationId && !d.aiSnapshot);
    for (const o of missing) {
      const id = o.observationId as string;
      setEnrichingIds((prev) => new Set(prev).add(id));
      try {
        const ctx = await TeamBApi.getObservation(id);
        setObservations((prev) =>
          prev.map((d) =>
            d.observationId === id ? { ...d, aiSnapshot: ctx.aiSnapshot ?? null } : d,
          ),
        );
      } catch {
        // 单条补充失败不阻断
      } finally {
        setEnrichingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    }
  }, []);

  const loadObservations = useCallback(async (pid: string) => {
    setLoadingObservations(true);
    setError(null);
    try {
      const data = await TeamBApi.timeline(pid);
      setObservations(data.days);
      void enrichMissingSnapshots(data.days);
      return data.days;
    } catch (e) {
      setObservations([]);
      setError(friendlyError(e));
      return [];
    } finally {
      setLoadingObservations(false);
    }
  }, [enrichMissingSnapshots]);

  const loadContext = useCallback(async (obsId: string) => {
    setLoadingContext(true);
    setError(null);
    try {
      const ctx = await TeamBApi.getObservation(obsId);
      setContext(ctx);
      return ctx;
    } catch (e) {
      setContext(null);
      setError(friendlyError(e));
      return null;
    } finally {
      setLoadingContext(false);
    }
  }, []);

  const loadSavedAdvice = useCallback(async (obsId: string) => {
    setLoadingSavedAdvice(true);
    try {
      const saved = await TeamBApi.getSavedAdvice(obsId);
      if (saved) {
        setResult(saved);
        setResultFromSaved(true);
        return saved;
      }
      setResult(null);
      setResultFromSaved(false);
      return null;
    } catch {
      setResult(null);
      setResultFromSaved(false);
      return null;
    } finally {
      setLoadingSavedAdvice(false);
    }
  }, []);

  const runDiagnosis = useCallback(async (obsId: string, refreshPlantId?: string) => {
    setDiagnosing(true);
    setError(null);
    setResult(null);
    setResultFromSaved(false);
    try {
      const r = await TeamBApi.careAdvice({ observationId: obsId });
      setResult(r);
      setResultFromSaved(false);
      const pid = refreshPlantId ?? plantId;
      if (pid) await loadObservations(pid);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setDiagnosing(false);
    }
  }, [plantId, loadObservations]);

  const selectObservation = useCallback(
    async (obsId: string) => {
      setSelectedObsId(obsId);
      setResult(null);
      setResultFromSaved(false);
      setError(null);
      const [ctx] = await Promise.all([loadContext(obsId), loadSavedAdvice(obsId)]);
      if (ctx?.aiSnapshot) {
        setObservations((prev) =>
          prev.map((d) =>
            d.observationId === obsId ? { ...d, aiSnapshot: ctx.aiSnapshot ?? null } : d,
          ),
        );
      }
    },
    [loadContext, loadSavedAdvice],
  );

  // 初始化:加载植物 + 解析 URL / session 默认选中
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await loadPlants();
      if (cancelled) return;

      const hint = readLastObservationHint();
      let nextPlantId = hint?.plantId && list.some((p) => p.id === hint.plantId)
        ? hint.plantId
        : list[0]?.id ?? '';

      let nextObsId: string | null = null;

      if (obsIdFromUrl) {
        const ctx = await TeamBApi.getObservation(obsIdFromUrl).catch(() => null);
        if (cancelled) return;
        if (ctx) {
          nextPlantId = ctx.plantId;
          nextObsId = obsIdFromUrl;
          setContext(ctx);
        }
      } else if (
        hint?.plantId &&
        list.some((p) => p.id === hint.plantId)
      ) {
        nextPlantId = hint.plantId;
        nextObsId = hint.observationId ?? null;
      }

      if (!nextPlantId) {
        setInitDone(true);
        return;
      }

      setPlantId(nextPlantId);
      const days = await loadObservations(nextPlantId);
      if (cancelled) return;

      const validIds = new Set(
        days.filter((d) => d.observationId).map((d) => d.observationId as string),
      );
      if (nextObsId && !validIds.has(nextObsId)) nextObsId = null;

      if (nextObsId) {
        setSelectedObsId(nextObsId);
        if (!obsIdFromUrl) {
          await loadContext(nextObsId);
        }
        const saved = await TeamBApi.getSavedAdvice(nextObsId).catch(() => null);
        if (cancelled) return;
        if (saved) {
          setResult(saved);
          setResultFromSaved(true);
        } else if (autoDiagnoseFromUrl) {
          await runDiagnosis(nextObsId, nextPlantId);
        }
      }

      setInitDone(true);
    })();
    return () => {
      cancelled = true;
    };
    // 仅 URL obsId 变化时重新初始化
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [obsIdFromUrl]);

  // 切换植物 → 重新拉观察列表
  const onPlantChange = async (pid: string) => {
    setPlantId(pid);
    setSelectedObsId(null);
    setContext(null);
    setResult(null);
    setResultFromSaved(false);
    setError(null);
    await loadObservations(pid);
  };

  const onStartDiagnosis = () => {
    if (selectedObsId) void runDiagnosis(selectedObsId);
  };

  const selectedPlant = plants.find((p) => p.id === plantId);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="flex items-center gap-2 text-lg font-bold">
          <Stethoscope className="h-5 w-5 text-teamB-primary" /> AI 健康诊断
        </h1>
        <p className="text-xs text-slate-500">
          先选植物和观察记录,AI 将结合该次照片与记录给出 1 项最优先行动
        </p>
      </header>

      {loadingPlants && (
        <p className="flex items-center gap-1 text-xs text-slate-500">
          <Loader2 className="h-3 w-3 animate-spin" />
          加载植物列表…
        </p>
      )}

      {initDone && plants.length === 0 && (
        <div className="card space-y-2 text-sm text-slate-600">
          <p>还没有植物,无法诊断。</p>
          <Link to="/add" className="btn-primary inline-flex w-full justify-center">
            去添加植物
          </Link>
        </div>
      )}

      {plants.length > 0 && (
        <>
          <section className="card space-y-2">
            <label className="text-xs text-slate-500">① 选择植物</label>
            <select
              className="input"
              value={plantId}
              onChange={(e) => void onPlantChange(e.target.value)}
            >
              {plants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.location}
                </option>
              ))}
            </select>
            {selectedPlant && (
              <p className="text-[11px] text-slate-500">
                {selectedPlant.species} · 共 {sortedObservations.length} 条拍照记录
              </p>
            )}
          </section>

          <section className="card space-y-2">
            <label className="text-xs text-slate-500">② 选择观察记录</label>
            {loadingObservations ? (
              <p className="flex items-center gap-1 text-xs text-slate-500">
                <Loader2 className="h-3 w-3 animate-spin" />
                加载观察记录…
              </p>
            ) : sortedObservations.length === 0 ? (
              <div className="space-y-2 text-sm text-slate-600">
                <p>这株植物还没有带照片的观察记录。</p>
                <Link
                  to={plantId ? `/daily?plantId=${plantId}` : '/daily'}
                  className="text-teamB-primary underline"
                >
                  去拍照记录
                </Link>
              </div>
            ) : (
              <ul className="space-y-2">
                {sortedObservations.map((o) => {
                  const id = o.observationId as string;
                  const active = selectedObsId === id;
                  return (
                    <li key={id}>
                      <button
                        type="button"
                        onClick={() => void selectObservation(id)}
                        className={`flex w-full items-center gap-3 rounded-md border p-2 text-left transition ${
                          active
                            ? 'border-teamB-primary bg-teamB-primary/5 ring-1 ring-teamB-primary/30'
                            : 'border-slate-200 bg-white hover:border-teamB-primary/40'
                        }`}
                      >
                        <SafeImage
                          team="b"
                          imageUrl={o.photoUrl}
                          imageKey={o.photoKey}
                          alt={`第${o.recordIndex}次记录`}
                          className="h-14 w-14 shrink-0 rounded-md object-cover ring-1 ring-slate-200"
                          placeholderClassName="flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-teamB-light/30 text-slate-400"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-800">
                            {o.createdAt ? formatDateTime(o.createdAt) : '未知时间'}
                            <span className="ml-1.5 text-xs font-normal text-slate-500">
                              第 {o.recordIndex} 次
                            </span>
                            {active && (
                              <span className="ml-2 badge bg-teamB-primary/10 text-teamB-primary text-[10px]">
                                已选
                              </span>
                            )}
                            {o.adviceSummary && (
                              <span className="ml-1.5 badge bg-emerald-100 text-[10px] text-emerald-700">
                                已诊断
                              </span>
                            )}
                            {o.careActions.includes('完成今日任务') ||
                            (o.careActions.length === 1 && o.careActions[0] === '继续观察') ? (
                              <span className="ml-1.5 badge bg-emerald-600 text-[10px] text-white">
                                任务已完成
                              </span>
                            ) : null}
                          </p>
                          <p className="text-[11px] text-slate-500">
                            {o.speciesMismatch
                              ? `⚠️ 照片像 ${o.speciesMismatch.detected}`
                              : o.soil
                                ? `土壤 ${o.soil}`
                                : ''}
                            {!o.speciesMismatch && o.lightPosition ? ` · 光照 ${o.lightPosition}` : ''}
                            {!o.speciesMismatch && o.leafState ? ` · 叶片 ${o.leafState}` : ''}
                          </p>
                          <ObservationAiSummary
                            snapshot={o.aiSnapshot}
                            compact
                            loading={enrichingIds.has(id)}
                          />
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      )}

      {(loadingContext || loadingSavedAdvice) && (
        <p className="flex items-center gap-1 text-xs text-slate-500">
          <Loader2 className="h-3 w-3 animate-spin" />
          {loadingSavedAdvice ? '加载历史诊断…' : '加载记录详情…'}
        </p>
      )}

      {context && selectedObsId && (
        <section className="card space-y-3">
          <h2 className="text-sm font-semibold text-slate-700">③ 本次诊断依据</h2>
          <div className="flex gap-3">
            <SafeImage
              team="b"
              imageUrl={context.photoUrl}
              imageKey={context.photoKey}
              alt={context.plant.name}
              className="h-24 w-24 shrink-0 rounded-md object-cover ring-1 ring-slate-200"
              placeholderClassName="flex h-24 w-24 shrink-0 items-center justify-center rounded-md bg-teamB-light/30 text-slate-400"
            />
            <div className="min-w-0 flex-1 space-y-1 text-sm">
              <p className="font-semibold text-teamB-primary">
                🌱 {context.plant.name}
                <span className="ml-1 text-xs font-normal text-slate-500">
                  ({context.plant.species})
                </span>
              </p>
              <p className="text-[11px] text-slate-500">摆放:{context.plant.location}</p>
              <p className="text-[11px] text-slate-500">
                {context.createdAt ? formatDateTime(context.createdAt) : '最近观察'}
                <span className="text-slate-400"> · 第 {context.recordIndex ?? context.day} 次记录</span>
              </p>
              <p className="text-[11px] text-slate-600">
                浇水:{context.wateredToday ?? '未填'} · 土壤:{context.soil ?? '未填'} · 光照:
                {context.lightPosition ?? '未填'}
              </p>
              {context.note && (
                <p className="text-[11px] text-slate-500">补充:{context.note}</p>
              )}
            </div>
          </div>

          {context.aiSnapshot && (
            <ObservationAiSummary snapshot={context.aiSnapshot} />
          )}

          {!result && !diagnosing && (
            <button
              type="button"
              onClick={onStartDiagnosis}
              disabled={!selectedObsId}
              className="btn-primary w-full"
            >
              开始 AI 诊断
            </button>
          )}
        </section>
      )}

      {diagnosing && (
        <p className="flex items-center gap-1 text-xs text-slate-500">
          <Loader2 className="h-3 w-3 animate-spin" />
          AI 正在结合照片分析,通常需要 30~60 秒,请稍候…
        </p>
      )}

      {error && !result && (
        <div className="card space-y-2 text-sm text-slate-600">
          <p>{error}</p>
          <div className="flex flex-wrap gap-2">
            {selectedObsId && (
              <button
                type="button"
                onClick={onStartDiagnosis}
                className="btn-outline inline-flex items-center gap-1 text-sm"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                重试
              </button>
            )}
            <Link
              to={plantId ? `/daily?plantId=${plantId}` : '/daily'}
              className="text-teamB-primary underline"
            >
              去拍照记录
            </Link>
          </div>
        </div>
      )}

      {result && (
        <>
          <section className="card space-y-2 border-teamB-primary/30 bg-teamB-primary/5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="flex items-center gap-1 text-sm font-semibold text-teamB-primary">
                🌱 {result.plant_name} · 今日养护卡
              </h2>
              {resultFromSaved && result.diagnosedAt && (
                <span className="badge bg-white text-[10px] text-slate-500">
                  已保存 · {formatDateTime(result.diagnosedAt)}
                </span>
              )}
            </div>
            <p className="text-[11px] text-slate-500">
              土壤 {result.soil_status} · 上次浇水 {result.last_watered} · 光照{' '}
              {result.light_position}
            </p>
            <ul className="list-inside list-disc text-sm">
              {result.today_observation.map((o, i) => (
                <li key={i}>{o}</li>
              ))}
            </ul>
            <div className="rounded-md bg-white p-3 text-sm">
              <p className="text-[11px] text-slate-500">🩺 今天最该做的一件事</p>
              <p className="mt-1 text-base font-semibold text-teamB-primary">
                {result.today_action}
              </p>
            </div>
            {result.reminder && (
              <p className="text-xs text-slate-600">💡 {result.reminder}</p>
            )}
            {result.uncertainty && (
              <div className="rounded-md bg-teamA-warning/10 px-3 py-2 text-xs text-teamA-warning">
                <AlertTriangle className="mr-1 inline h-3 w-3" />
                {result.uncertainty}
              </div>
            )}
            {result.fallback && (
              <p className="text-[11px] text-amber-700">
                本次为保守建议(AI 暂时不可用),可稍后点「重试」获取完整诊断。
              </p>
            )}
          </section>

          {context && selectedObsId && (
            <CareTaskPanel
              embedded
              plantId={context.plantId}
              plantName={context.plant.name}
              observationId={selectedObsId}
              todayAction={result.today_action}
              reminder={result.reminder}
              photoUrl={context.photoUrl}
              photoKey={context.photoKey}
              onCompleted={() => void loadObservations(plantId)}
            />
          )}

          {!diagnosing && (
            <button
              type="button"
              className="btn-outline inline-flex w-full items-center justify-center gap-1 text-sm"
              onClick={onStartDiagnosis}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              重新诊断
            </button>
          )}

          <Link
            to={`/timeline?plantId=${plantId}`}
            className="btn-outline w-full text-center text-sm"
          >
            去看养护日记
          </Link>
        </>
      )}
    </div>
  );
}
